import React, { useState, useEffect } from "react";
import AdminLayout from "./AdminLayout";
import ApiService from "../../services/api";
import {
  Search,
  Filter,
  Download,
  CreditCard,
  User,
  Package,
  Building2,
  DollarSign,
  Calendar,
  FileText,
  Eye,
  Mail,
  Phone,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";

interface Order {
  id: string;
  order_id: string;
  patient_id: number;
  patient_name: string;
  clinic_id: number;
  merchant_name: string;
  email: string;
  phone: string;
  first_name: string;
  last_name: string;
  amount: number;
  currency: string;
  payment_status:
    | "CAPTURED"
    | "AUTHORISED"
    | "PENDING"
    | "FAILED"
    | "REFUSED"
    | "DECLINED"
    | "ERROR";
  cardname: string;
  cardnumber: string;
  cardtype: string;
  created_at: string;
  products: Array<{
    product_id: number;
    product_name: string;
    patient_product_price_id: number;
    final_price: number;
  }>;
}

interface Clinic {
  clinic_id: number;
  clinic_name: string;
}

interface Product {
  product_id: number;
  name: string;
}

const AdminOrders: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isPending = searchParams.get("is_pending") === "true";
  const [orders, setOrders] = useState<Order[]>([]);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [downloadingReceipt, setDownloadingReceipt] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterProduct, setFilterProduct] = useState<number | "">("");
  const [filterClinic, setFilterClinic] = useState<number | "">("");
  const [filterStatus, setFilterStatus] = useState<
    | "all"
    | "CAPTURED"
    | "AUTHORISED"
    | "PENDING"
    | "FAILED"
    | "REFUSED"
    | "DECLINED"
    | "ERROR"
  >("all");
  const [orderIdFilter, setOrderIdFilter] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  useEffect(() => {
    if (isPending) {
      setFilterStatus("AUTHORISED");
    }
  }, [isPending]);

  useEffect(() => {
    fetchOrders();
  }, [
    currentPage,
    searchTerm,
    filterProduct,
    filterClinic,
    filterStatus,
    orderIdFilter,
  ]);

  useEffect(() => {
    fetchClinics();
    fetchProducts();
  }, []);

  const fetchOrders = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: any = {
        page: currentPage,
        pageSize: pageSize,
      };

      // Add filters if they have values
      if (searchTerm.trim()) {
        params.search_text = searchTerm.trim();
      }

      if (filterProduct) {
        params.product_id = filterProduct;
      }

      if (filterClinic) {
        params.clinic_id = filterClinic;
      }

      if (orderIdFilter.trim()) {
        params.order_id = orderIdFilter.trim();
      }

      if (filterStatus !== "all") {
        params.payment_status = filterStatus;
      }

      const response = await ApiService.getOrders(params);

      if (response.success) {
        // Filter by payment status on frontend since API doesn't seem to support it
        let filteredOrders = response.data;
        if (filterStatus !== "all") {
          filteredOrders = response.data.filter(
            (order: Order) => order.payment_status === filterStatus
          );
        }

        setOrders(filteredOrders);
        setTotalPages(response.totalPages);
        setTotalItems(response.total);
      } else {
        throw new Error(response.message || "Failed to fetch orders");
      }
    } catch (err: any) {
      console.error("Error fetching orders:", error);
      setError(
        err?.response?.data?.message || err?.message || "Failed to fetch orders"
      );
      setOrders([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchClinics = async () => {
    try {
      const response = await ApiService.getClinics({});
      if (response.success) {
        setClinics(response.data);
      }
    } catch (error) {
      console.error("Error fetching clinics:", error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await ApiService.getProductsList();
      if (response.success) {
        setProducts(response.data);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleDownloadReceipt = async (orderId: string) => {
    setDownloadingReceipt(orderId);
    try {
      const blob = await ApiService.getOrderReceipt(orderId);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `receipt-${orderId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Error downloading receipt:", error);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to download receipt"
      );
    } finally {
      setDownloadingReceipt(null);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
      case "APPROVED":
      case "CAPTURED":
        return "bg-green-100 text-green-800";
      case "PENDING":
      case "AUTHORISED":
        return "bg-yellow-100 text-yellow-800";
      case "FAILED":
      case "REFUSED":
      case "DECLINED":
      case "ERROR":
        return "bg-red-100 text-red-800";
      case "CANCELLED":
      case "REFUNDED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (type: string, value: any) => {
    setCurrentPage(1); // Reset to first page when filtering
    switch (type) {
      case "product":
        setFilterProduct(value);
        break;
      case "clinic":
        setFilterClinic(value);
        break;
      case "payment_status":
        setFilterStatus(value);
        break;
      case "orderId":
        setOrderIdFilter(value);
        break;
    }
  };

  // Mobile-friendly Order Card Component
  const OrderCard = ({ order }: { order: Order }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-4 w-full overflow-hidden">
      {/* Order Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              Order #{order.order_id}
            </h3>
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${getStatusColor(
                order.payment_status
              )}`}
            >
              {order.payment_status}
            </span>
          </div>
          <div className="space-y-1">
            <div className="text-sm text-gray-600 flex items-center min-w-0">
              <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{formatDate(order.created_at)}</span>
            </div>
            <div className="text-sm text-gray-600 flex items-center min-w-0">
              <Building2 className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{order.merchant_name}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1 flex-shrink-0">
          <button
            onClick={() => handleDownloadReceipt(order.order_id)}
            disabled={downloadingReceipt === order.order_id}
            className="text-blue-600 hover:text-blue-900 p-2 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
            title="Download Receipt"
          >
            {downloadingReceipt === order.order_id ? (
              <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            ) : (
              <Download className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* Customer Info */}
      <div className="border-t border-gray-200 pt-3">
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Customer
        </h4>
        <div className="flex items-start space-x-3">
          <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <User className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {order.patient_name}
            </div>
            <div className="text-sm text-gray-600 flex items-center min-w-0">
              <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">{order.email}</span>
            </div>
            {order.phone && (
              <div className="text-sm text-gray-600 flex items-center min-w-0">
                <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                <span className="truncate">{order.phone}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="border-t border-gray-200 pt-3">
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Products
        </h4>
        <div className="space-y-2">
          {order.products.map((product, index) => (
            <div
              key={index}
              className="flex items-center justify-between gap-2 min-w-0"
            >
              <div className="flex items-center min-w-0 flex-1">
                <Package className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-gray-900 truncate">
                  {product.product_name}
                </span>
              </div>
              <span className="text-sm text-gray-600 flex-shrink-0">
                {formatCurrency(product.final_price, order.currency)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Info */}
      <div className="border-t border-gray-200 pt-3">
        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
          Payment
        </h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium text-gray-900 flex items-center">
              <DollarSign className="h-3 w-3 mr-1" />
              Total Amount
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {formatCurrency(order.amount, order.currency)}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 min-w-0">
            <div className="text-sm text-gray-600 flex items-center min-w-0">
              <CreditCard className="h-3 w-3 mr-1 flex-shrink-0" />
              <span className="truncate">
                {order.cardtype} ****{order.cardnumber.slice(-4)}
              </span>
            </div>
          </div>
          {order.cardname && (
            <div className="text-sm text-gray-600 truncate">
              Cardholder: {order.cardname}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout title="Orders">
      {/* Main container with proper overflow handling */}
      <div className="space-y-4 sm:space-y-6 w-full min-w-0">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div className="text-sm text-red-700 break-words">{error}</div>
              <button
                onClick={() => setError(null)}
                className="ml-4 text-red-700 hover:text-red-900 flex-shrink-0"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Orders
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Total {totalItems}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-col-4 2xl:grid-col-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Order ID */}
            <div>
              <input
                type="text"
                placeholder="Order ID..."
                value={orderIdFilter}
                onChange={(e) => handleFilterChange("orderId", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>

            {/* Product Filter */}
            <div>
              <select
                value={filterProduct}
                onChange={(e) =>
                  handleFilterChange(
                    "product",
                    e.target.value ? parseInt(e.target.value) : ""
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
              >
                <option value="">All Products</option>
                {products.map((product) => (
                  <option key={product.product_id} value={product.product_id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Clinic Filter */}
            <div>
              <select
                value={filterClinic}
                onChange={(e) =>
                  handleFilterChange(
                    "clinic",
                    e.target.value ? parseInt(e.target.value) : ""
                  )
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
              >
                <option value="">All Clinics</option>
                {clinics.map((clinic) => (
                  <option key={clinic.clinic_id} value={clinic.clinic_id}>
                    {clinic.clinic_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <select
                value={filterStatus}
                onChange={(e) =>
                  handleFilterChange("payment_status", e.target.value)
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="CAPTURED">CAPTURED</option>
                <option value="AUTHORISED">AUTHORISED</option>
                <option value="PENDING">PENDING</option>
                <option value="REFUSED">REFUSED</option>
                <option value="DECLINED">DECLINED</option>
                <option value="FAILED">FAILED</option>
                <option value="ERROR">ERROR</option>
              </select>
            </div>
          </div>
        </div>

        {/* Orders Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 w-full overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order Details
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Products
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              Order #{order.order_id}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                {formatDate(order.created_at)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Building2 className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                {order.merchant_name}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                              <User className="h-5 w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {order.patient_name}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {order.email}
                              </div>
                              {order.phone && (
                                <div className="text-sm text-gray-500 truncate">
                                  {order.phone}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="space-y-1 min-w-0">
                            {order.products.map((product, index) => (
                              <div
                                key={index}
                                className="flex items-center text-sm min-w-0"
                              >
                                <Package className="h-3 w-3 mr-1 text-gray-400 flex-shrink-0" />
                                <span className="text-gray-900 truncate">
                                  {product.product_name}
                                </span>
                                <span className="ml-2 text-gray-500 flex-shrink-0">
                                  (
                                  {formatCurrency(
                                    product.final_price,
                                    order.currency
                                  )}
                                  )
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900 flex items-center">
                              <DollarSign className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                {formatCurrency(order.amount, order.currency)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <CreditCard className="h-3 w-3 mr-1 flex-shrink-0" />
                              <span className="truncate">
                                {order.cardtype} ****
                                {order.cardnumber.slice(-4)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500 truncate">
                              {order.cardname}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              order.payment_status
                            )}`}
                          >
                            {order.payment_status}
                          </span>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() =>
                                handleDownloadReceipt(order.order_id)
                              }
                              disabled={downloadingReceipt === order.order_id}
                              className="text-blue-600 hover:text-blue-900 p-1 disabled:opacity-50"
                              title="Download Receipt"
                            >
                              {downloadingReceipt === order.order_id ? (
                                <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile/Tablet Card View */}
              <div className="lg:hidden p-4 space-y-4 w-full overflow-hidden">
                {orders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>

              {orders.length === 0 && !isLoading && (
                <div className="text-center py-12 px-4">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No orders found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchTerm ||
                    filterProduct ||
                    filterClinic ||
                    filterStatus !== "all" ||
                    orderIdFilter
                      ? "Try adjusting your search or filter criteria."
                      : "No orders have been placed yet."}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
